'use client';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AuthContext } from './Authentication';
import { SessionContext } from './Session';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { addIcon } from '@iconify/react';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { getImpersonationSession, stopImpersonation } from '@/services/userLoginApi';
import { clearRBACScopeStorage } from '@/lib/rbacScopeStorage';
import { broadcastImpersonationState } from './ImpersonationContext';
import type { UserListItem } from '@/types/user-login';
import { BRAND, STATUS } from '@/theme/brand-tokens';

// Iconify fetches icons from its web API at runtime; when that request fails the icon renders
// empty — which here would make the only way out of impersonation an invisible button.
// Registering the exit icon's SVG locally means Iconify renders it without any network fetch.
addIcon(UI_ICONS.close, {
  body: '<path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/>',
  width: 24,
  height: 24,
});

// Authentication.js/Session.js are untyped (.js, predate the TS migration) — cast their
// context values to the slice this component actually uses rather than trusting `any`.
interface AuthContextValue {
  logout: (force?: boolean) => void;
  setAuthData: (data: { userId: string; token: string; isAuthenticated: boolean }) => void;
}

interface SessionContextValue {
  user: { firstname?: string; email?: string } | null;
}

export default function ImpersonationMenu() {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [targetUser, setTargetUser] = useState<UserListItem | null>(null);
  const [stopping, setStopping] = useState(false);
  const { logout, setAuthData } = useContext(AuthContext) as AuthContextValue;
  const { user } = useContext(SessionContext) as SessionContextValue;
  const router = useRouter();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Hoisted out of the effect (rather than a local closure) so both the mount-time check
  // and the impersonation-change/storage listeners below can share one code path — a client-
  // side identity swap (see switch-user/page.tsx, no page reload) never remounts this
  // component, so it's the only way this banner learns impersonation just started or stopped.
  const abortControllerRef = useRef<AbortController | null>(null);
  const checkImpersonation = useCallback(() => {
    // Abort the previous check so an older "still impersonating" response can't land after a
    // newer one (e.g. right after exit) and bring the banner back / rewrite localStorage.userId.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    getImpersonationSession({ signal: controller.signal })
      .then((session) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        if (session.is_impersonating) {
          setIsImpersonating(true);
          setTargetUser(session.target_user || null);
          // Reconcile localStorage in case it was out of sync
          localStorage.setItem('impersonation', 'true');
          if (session.active_user_id) {
            localStorage.setItem('userId', String(session.active_user_id));
          }
          // Impersonation started in another tab/app never set originalUserId here — without
          // it, exiting has no admin id to restore.
          if (session.real_user_id && !localStorage.getItem('originalUserId')) {
            localStorage.setItem('originalUserId', String(session.real_user_id));
          }
        } else {
          // Session no longer active on the server — clean up this tab
          const wasImpersonating =
            localStorage.getItem('impersonation') === 'true' ||
            !!localStorage.getItem('originalUserId');

          if (wasImpersonating) {
            const originalUserId = localStorage.getItem('originalUserId');
            _cleanupLocalStorage(originalUserId);
            broadcastImpersonationState(false);
          }

          if (mountedRef.current) {
            setIsImpersonating(false);
            setTargetUser(null);
          }
        }
      })
      .catch(() => {
        if (!mountedRef.current || controller.signal.aborted) return;
        // Fall back to localStorage when the API is unreachable
        setIsImpersonating(localStorage.getItem('impersonation') === 'true');
      });
  }, []);

  useEffect(() => {
    // Only re-check when the impersonation key specifically changes (cross-tab)
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== 'impersonation' && e.key !== null) return;
      checkImpersonation();
    };

    // Same-tab broadcast from broadcastImpersonationState() — re-check with the server on
    // both start and stop rather than trusting the event's own `active` flag, so the banner
    // (and target user name) always reflects real session state.
    const handleCustom = () => {
      if (!mountedRef.current) return;
      checkImpersonation();
    };

    checkImpersonation();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('impersonation-change', handleCustom);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('impersonation-change', handleCustom);
    };
  }, [checkImpersonation]);

  const handleReturnToPreviousUser = async () => {
    if (stopping) return;
    setStopping(true);
    try {
      const originalUserId = localStorage.getItem('originalUserId');

      // Drop any in-flight session check — it was issued while still impersonating.
      abortControllerRef.current?.abort();
      const res = await stopImpersonation();

      // Restore admin localStorage state before updating context/navigating — anything
      // that still reads localStorage directly should see the restored admin identity too.
      // Fall back to the id the server says it restored when originalUserId is missing.
      const restoredId = String(originalUserId || res?.userID || '');
      if (restoredId) {
        localStorage.setItem('token', restoredId);
        localStorage.setItem('userId', restoredId);
      }
      _cleanupLocalStorage(null); // removes all impersonation keys (not token/userId — already restored above)
      clearRBACScopeStorage();

      // Client-side identity swap (no full page reload): setAuthData updates AuthContext,
      // which SessionContext already reacts to (re-fetches user details on userId change);
      // broadcastImpersonationState fires the event RBACContext listens for to reset and
      // re-fetch org/permission state for the restored admin. router.push (not
      // window.location.href) applies Next.js's basePath automatically — do not prefix
      // with /v2 here.
      if (restoredId) {
        setAuthData({ userId: restoredId, token: restoredId, isAuthenticated: true });
      }
      setIsImpersonating(false);
      setTargetUser(null);
      broadcastImpersonationState(false);
      router.push('/dashboard');
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      logout(true);
    } finally {
      if (mountedRef.current) setStopping(false);
    }
  };

  if (!isImpersonating) return null;

  // Always the full banner, regardless of sidebar state: it renders in the main content area
  // (not the sidebar), so it has room either way. A former icon-only variant for the mini nav
  // had no exit button — and the nav is forced to mini on /ai-agent and admin fullscreen
  // paths (sticky afterwards), while Sign out is disabled during impersonation, which left
  // users with no way out.
  return (
    <Box
      sx={{
        backgroundColor: BRAND.yellowLight,
        color: BRAND.navy,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: '8px 1em 1em 1em',
        borderRadius: '.416em',
        border: `1px solid ${STATUS.light.warning.text}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '.6em .3em',
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        <Iconify
          icon={UI_ICONS.warning}
          width={20}
          sx={{ color: STATUS.light.warning.text, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
        />
        <Box>
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8rem', display: 'block', fontWeight: 700, lineHeight: 1.2, color: BRAND.navy }}
          >
            <strong>Impersonating</strong>
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: '0.75rem', display: 'block', fontWeight: 400, lineHeight: 1.2, color: BRAND.navy }}
          >
            {targetUser?.firstname || user?.firstname || targetUser?.email || user?.email || 'User'}
          </Typography>
        </Box>
      </Box>

      <Tooltip title="Exit impersonation" arrow placement="left">
        {/* span wrapper: MUI Tooltip needs an enabled child to receive hover events */}
        <span>
          <IconButton
            onClick={handleReturnToPreviousUser}
            disabled={stopping}
            size="small"
            aria-label="Exit impersonation"
            sx={{
              color: STATUS.light.warning.text,
              borderRadius: '4px',
              opacity: stopping ? 0.5 : 1,
              '&:hover': { backgroundColor: STATUS.light.warning.bg },
            }}
          >
            <Iconify icon={UI_ICONS.close} width={18} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

// Removes impersonation-specific keys. token/userId are NOT removed here
// because the caller restores them before navigating.
function _cleanupLocalStorage(originalUserId: string | null) {
  localStorage.removeItem('impersonation');
  localStorage.removeItem('originalUserId');
  localStorage.removeItem('lastJourneyId');
  // Legacy keys kept for safety (may have been set by older code paths)
  localStorage.removeItem('originalUser');
  localStorage.removeItem('targetUser');
  localStorage.removeItem('originalUserToken');
  localStorage.removeItem('originalUserCartId');
}
